import { Head, router, usePoll } from '@inertiajs/react';
import {
  CheckCircle2,
  ExternalLink,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toastFlash } from '@/lib/flash-toast';
import type { AdminJudgingIndex, JudgeAdmin } from '@/types';
import { githubUsername } from '../Mentors/components/githubUsername';
import { CriteriaEditor } from './components/CriteriaEditor';
import { JudgeFormDialog } from './components/JudgeFormDialog';
import { PublishResults } from './components/PublishResults';
import { StageRankings } from './components/StageRankings';

export default function Index({
  judges,
  stages,
  criteria,
  rankings,
  resultsPublishedAt,
  eventSlug,
  eventName,
  title,
}: AdminJudgingIndex) {
  // The rankings are live: re-read just them while the judges grade.
  usePoll(10000, { only: ['rankings'] });
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const editJudge = judges.find((judge) => judge.id === editId) ?? null;
  const stageName = (id: string) =>
    stages.find((stage) => stage.id === id)?.name ?? '?';
  const [reopen, setReopen] = useState<{
    judge: JudgeAdmin;
    stageId: string;
  } | null>(null);

  const handleReopen = () => {
    if (!reopen) return;

    router.delete(
      `/admin/${eventSlug}/judging/judges/${reopen.judge.id}/stages/${reopen.stageId}/completion`,
      {
        preserveScroll: true,
        onSuccess: (page) => toastFlash(page, 'Stage reopened'),
        onError: () =>
          toast({ type: 'error', description: 'Failed to reopen stage' }),
        onFinish: () => setReopen(null),
      },
    );
  };

  const handleDelete = () => {
    if (!deleteId) return;

    setIsDeleting(true);
    router.delete(`/admin/${eventSlug}/judging/judges/${deleteId}`, {
      preserveScroll: true,
      onSuccess: (page) => toastFlash(page, 'Judge deleted successfully'),
      onError: () =>
        toast({ type: 'error', description: 'Failed to delete judge' }),
      onFinish: () => {
        setIsDeleting(false);
        setDeleteId(null);
      },
    });
  };

  return (
    <>
      <Head title={title} />
      <div className="container mx-auto flex flex-col gap-6 py-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-bold text-3xl tracking-tight">Judging</h1>
            <p className="text-muted-foreground">
              The judges of {eventName} and the criteria they grade on. Judges
              sign in at /judges with their GitHub account.
            </p>
          </div>
          <PublishResults
            eventSlug={eventSlug}
            publishedAt={resultsPublishedAt}
            rankings={rankings}
          />
        </div>

        <StageRankings rankings={rankings} />

        <CriteriaEditor
          key={criteria.map((c) => `${c.id}:${c.name}:${c.weight}`).join('|')}
          eventSlug={eventSlug}
          criteria={criteria}
        />

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Judges ({judges.length})</CardTitle>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Judge
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {judges.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p>No judges yet. Add one to get started.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>GitHub</TableHead>
                    <TableHead>LinkedIn</TableHead>
                    <TableHead>Company / Title</TableHead>
                    <TableHead>Stages</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {judges.map((judge) => (
                    <TableRow key={judge.id}>
                      <TableCell className="font-medium">
                        {judge.fullName}
                      </TableCell>
                      <TableCell>
                        <a
                          href={judge.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                        >
                          {githubUsername(judge.github)}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </TableCell>
                      <TableCell>
                        {judge.linkedin ? (
                          <a
                            href={judge.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                          >
                            LinkedIn
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {judge.companyTitle || (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {judge.stageIds.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {judge.stageIds.map((id) =>
                              judge.completedStageIds.includes(id) ? (
                                <div key={id} className="flex items-center gap-1">
                                  <Badge className="gap-1">
                                    <CheckCircle2 className="h-3 w-3" />
                                    {stageName(id)} · closed
                                  </Badge>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() =>
                                      setReopen({ judge, stageId: id })
                                    }
                                  >
                                    <RotateCcw className="mr-1 h-3 w-3" />
                                    Reopen
                                  </Button>
                                </div>
                              ) : (
                                <Badge
                                  key={id}
                                  variant="secondary"
                                  className="w-fit"
                                >
                                  {stageName(id)}
                                </Badge>
                              ),
                            )}
                          </div>
                        ) : (
                          <span className="text-destructive text-sm">
                            None — sees nothing
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            aria-label={`Edit ${judge.fullName}`}
                            onClick={() => setEditId(judge.id)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            aria-label={`Delete ${judge.fullName}`}
                            onClick={() => setDeleteId(judge.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {creating && (
        <JudgeFormDialog
          eventSlug={eventSlug}
          stages={stages}
          open
          onClose={() => setCreating(false)}
        />
      )}
      {editJudge && (
        <JudgeFormDialog
          key={editJudge.id}
          eventSlug={eventSlug}
          judge={editJudge}
          stages={stages}
          open
          onClose={() => setEditId(null)}
        />
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Judge</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this judge? They will lose access
              to /judges.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!reopen} onOpenChange={() => setReopen(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen stage</AlertDialogTitle>
            <AlertDialogDescription>
              {reopen &&
                `${reopen.judge.fullName} closed their grading of ${stageName(reopen.stageId)}. Reopening lets them change their grades and notes again; they will have to close it again.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReopen}>Reopen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
